import { useEffect, useMemo, useState } from 'react';
import { Alert, Modal, Radio, Space, Typography, Spin, Table, Tag, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../../api/users';
import { dataSourcesApi } from '../../../api/dataSources';
import { firmsApi } from '../../../api/firms';

interface Props {
  firmId: number;
  userId: number;
  firmRoleId: number | null;
  open: boolean;
  onClose: () => void;
}

type State = 'default' | 'allow' | 'deny';

export function UserPermissionsModal({ firmId, userId, firmRoleId, open, onClose }: Props) {
  const queryClient = useQueryClient();
  const [states, setStates] = useState<Record<number, State>>({});

  const reportsQuery = useQuery({
    queryKey: ['firm-data-sources', firmId],
    queryFn: () => dataSourcesApi.getByFirm(firmId),
    enabled: open && !!firmId,
  });

  const permsQuery = useQuery({
    queryKey: ['user-permissions', userId],
    queryFn: () => usersApi.getPermissions(userId),
    enabled: open && !!userId,
  });

  const roleQuery = useQuery({
    queryKey: ['role-permissions', firmId, firmRoleId],
    queryFn: () => firmsApi.getRolePermissions(firmId, firmRoleId as number),
    enabled: open && !!firmId && !!firmRoleId,
  });

  useEffect(() => {
    if (permsQuery.data) {
      const next: Record<number, State> = {};
      permsQuery.data.allow.forEach((id) => (next[id] = 'allow'));
      permsQuery.data.deny.forEach((id) => (next[id] = 'deny'));
      setStates(next);
    }
  }, [permsQuery.data, open]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const allow = Object.entries(states).filter(([, s]) => s === 'allow').map(([id]) => Number(id));
      const deny = Object.entries(states).filter(([, s]) => s === 'deny').map(([id]) => Number(id));
      return usersApi.updatePermissions(userId, { allow, deny });
    },
    onSuccess: () => {
      message.success('Kullanıcı izinleri kaydedildi.');
      queryClient.invalidateQueries({ queryKey: ['user-permissions', userId] });
      onClose();
    },
    onError: () => message.error('İzinler kaydedilemedi.'),
  });

  const reports = useMemo(() => reportsQuery.data ?? [], [reportsQuery.data]);
  const loading = reportsQuery.isLoading || permsQuery.isLoading;

  // What "Rolden" resolves to for a report, for an informational tag.
  // When firmRoleId is null: no role → backend fail-opens → all reports visible.
  const roleAllowsAll = roleQuery.data?.allReports ?? false;
  const roleSourceIds = useMemo(() => new Set(roleQuery.data?.sourceIds ?? []), [roleQuery.data]);
  const roleBaseline = (id: number) =>
    firmRoleId === null ? 'Görünür' : (roleAllowsAll || roleSourceIds.has(id) ? 'Görünür' : 'Gizli');

  const setState = (id: number, s: State) => setStates((p) => ({ ...p, [id]: s }));

  const columns = [
    { title: 'Rapor', dataIndex: 'name', key: 'name' },
    {
      title: 'Rolden',
      key: 'baseline',
      render: (_: unknown, r: { id: number }) => <Tag>{roleBaseline(r.id)}</Tag>,
    },
    {
      title: 'Override',
      key: 'state',
      render: (_: unknown, r: { id: number }) => (
        <Radio.Group
          size="small"
          value={states[r.id] ?? 'default'}
          onChange={(e) => setState(r.id, e.target.value)}
        >
          <Radio.Button value="default">Rolden</Radio.Button>
          <Radio.Button value="allow">Açık</Radio.Button>
          <Radio.Button value="deny">Kapalı</Radio.Button>
        </Radio.Group>
      ),
    },
  ];

  return (
    <Modal
      title="Kullanıcı Rapor İzinleri"
      open={open}
      onCancel={onClose}
      onOk={() => saveMutation.mutate()}
      okText="Kaydet"
      cancelText="İptal"
      confirmLoading={saveMutation.isPending}
      width={640}
    >
      {loading ? (
        <Spin />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          {firmRoleId === null && (
            <Alert
              type="warning"
              showIcon
              message={`Bu kullanıcının rolü yok — varsayılan olarak tüm raporları görür. Yalnızca "Kapalı" override'ları gizler.`}
            />
          )}
          <Typography.Text type="secondary">
            "Rolden" = rolün izni geçerli. "Açık" raporu kullanıcıya ekler, "Kapalı" (önceliklidir) gizler.
          </Typography.Text>
          <Table rowKey="id" size="small" pagination={false} columns={columns} dataSource={reports} />
        </Space>
      )}
    </Modal>
  );
}
